import {
    User,
    UserManager,
    WebStorageStateStore,
    InMemoryWebStorage,
    type IdTokenClaims,
    type UserManagerSettings,
    Log as OidcLog,
    ErrorResponse,
} from 'oidc-client-ts';
import join from 'lodash/fp/join';
import { mapUserProfile } from './userProfile';
import { config } from '../../config';
import type { OAuthConfig } from '..';
import type { SessionRenewedResult } from '../types';
import jwtDecode from 'jwt-decode';

export const initializeOidcLogging = () => {
    // enable logging for oidc-client
    if (localStorage.getItem('oidcDebug') === 'enabled') {
        OidcLog.setLogger(console);
        OidcLog.setLevel(OidcLog.DEBUG);
    }
};

const isErrorResponse = (error: unknown): error is ErrorResponse => error instanceof ErrorResponse;

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const retrySigninSilent = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.signinSilent().catch((error: unknown) => {
        if (isErrorResponse(error) && error.error === 'login_required') {
            oauthConfig.onSessionExpired();
        } else {
            setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
        }
    });
};

export const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
    accessToken: result.access_token,
    idToken: result.id_token ?? null,
    idTokenClaims: result.profile,
    locale: result.profile?.locale ?? 'en-GB',
    profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
    const redirectUri = config.login.redirectUri;
    const silentRedirectUri = config.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', config.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        monitorSession: true,
        staleStateAgeInSeconds: 600,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        filterProtocolClaims: false,
    };

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    initializeOidcLogging();

    userManager.events.addUserLoaded(user => {
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(() => {
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut(() => {
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

export const configureMockUserManager = (oauthConfig: OAuthConfig): UserManager => {
    if (import.meta.env.MODE !== 'test') {
        console.warn('[feature/login/oidc-session] Using mocked authorization due to config setting');
    }

    const signinSilent = () => {
        const accessToken = config.prodAccessToken || 'valid-mocked-oauth-bogus-token';
        const idToken = config.prodIdToken;

        const userSettings = {
            id_token: 'id_token',
            session_state: 'session_state',
            access_token: accessToken,
            refresh_token: 'refresh_token',
            token_type: 'token_type',
            scope: 'scope',
            profile: (idToken
                ? jwtDecode(idToken)
                : {
                      iss: 'Issuer Identifier',
                      aud: 'Audience(s): client_id',
                      exp: 10,
                      iat: 5,
                      account: 'mockaccount',
                      tenant: 'rio-eu.test',
                      azp: 'test-client',
                      email: 'test@example.com',
                      family_name: 'Client',
                      given_name: 'Test',
                      name: 'Test Client',
                      sub: 'prod-rio-users:mock-user',
                      acr: 'urn:rio:loa:2',
                      locale: config.login.mockLocale,
                  }) as IdTokenClaims,
            expires_at: 100000,
            state: 'state',
        };

        const user = new User(userSettings);
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
        return Promise.resolve(user);
    };

    const clearStaleState = () => {
        console.info('[configuration/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    return { signinSilent, clearStaleState } as UserManager;
};
